import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { format } from 'date-fns';
import StatisticsList from './StatisticsList';
import { recentBookingsKey } from '@dashboard/lib/queryKeys';
import { getPageTitle } from '@dashboard/lib/pageTitle';

function useRecentBookings() {
  const { sellerId } = useAuth();

  const { getCharges } = useChargeQueries();

  const { data, isError, isLoading } = useQuery({
    queryKey: recentBookingsKey(sellerId),
    queryFn: () =>
      getCharges(sellerId, {
        limit: 4,
        livemode: true,
        status: 'paid',
        pageType: 'event',
      }),
    staleTime: 1000 * 60, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    isLoading,
    isError,
    data,
  };
}

export default function RecentBookings() {
  const { data, isLoading, isError } = useRecentBookings();

  return (
    <StatisticsList.List>
      {isLoading && <StatisticsList.Loading />}

      {isError && <StatisticsList.EmptyState>Something went wrong</StatisticsList.EmptyState>}

      {!isLoading && !data?.length && (
        <StatisticsList.EmptyState>No bookings yet</StatisticsList.EmptyState>
      )}

      {data?.map(booking => (
        <StatisticsList.Item key={booking._id} href={`/bookings/${booking._id}`}>
          <StatisticsList.Left>
            <StatisticsList.PrimaryText>
              {booking.customer?.email || booking.customerEmail}
            </StatisticsList.PrimaryText>
            <StatisticsList.SecondaryText>
              {getPageTitle(booking.page)}
            </StatisticsList.SecondaryText>
          </StatisticsList.Left>

          <StatisticsList.Right>
            <StatisticsList.PrimaryText>
              {getPriceLabel({
                amount: booking.amount,
                currency: booking.currency,
              })}
            </StatisticsList.PrimaryText>

            <StatisticsList.SecondaryText>
              {format(new Date(booking.createdAt), 'MMM d, yyyy HH:mm')}
            </StatisticsList.SecondaryText>
          </StatisticsList.Right>
        </StatisticsList.Item>
      ))}
    </StatisticsList.List>
  );
}
